export const API_BASE = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5002/api' : '/api')).replace(/\/+$/, '');
export const SERVER_URL = (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : (window.location.hostname === 'localhost' ? 'http://localhost:5002' : '')).replace(/\/+$/, '');

// ── Simple Cache (memory + localStorage) ────────────────────────────────────
const memCache = {};
const CACHE_TTL = 60 * 1000; // 60 seconds

export const cache = {
  get(key) {
    const mem = memCache[key];
    if (mem && Date.now() - mem.ts < CACHE_TTL) return mem.data;
    try {
      const ls = localStorage.getItem('hw_' + key);
      if (ls) { const p = JSON.parse(ls); if (Date.now() - p.ts < CACHE_TTL * 5) return p.data; }
    } catch (_) {}
    return null;
  },
  set(key, data) {
    memCache[key] = { data, ts: Date.now() };
    try { localStorage.setItem('hw_' + key, JSON.stringify({ data, ts: Date.now() })); } catch (_) {}
  }
};

export const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${SERVER_URL}${url}`;
  return url;
};

export const api = {
  async getArtists(params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API_BASE}/artists${query ? `?${query}` : ''}`);
    if (!res.ok) throw new Error('Failed to fetch artists');
    const data = await res.json();
    return Array.isArray(data) ? data : (data.artists || []);
  },


  async getArtist(id) {
    const res = await fetch(`${API_BASE}/artists/${id}`);
    if (!res.ok) throw new Error('Failed to fetch artist');
    return res.json();
  },

  async getArtistByUserId(userId) {
    try {
      const res = await fetch(`${API_BASE}/artists/user/${userId}?t=${Date.now()}`, {
        cache: 'no-cache'
      });
      if (!res.ok) {
        // Fallback for new users
        return { name: 'New Artist', songs: [] };
      }
      return res.json();
    } catch (error) {
      console.error('API Error:', error);
      return { name: 'New Artist', songs: [] };
    }
  },

  async createArtist(data) {
    const res = await fetch(`${API_BASE}/artists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create artist');
    return res.json();
  },

  async updateArtist(id, data) {
    const res = await fetch(`${API_BASE}/artists/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update artist');
    return res.json();
  },

  async followArtist(id) {
    const res = await fetch(`${API_BASE}/artists/${id}/follow`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to follow artist');
    return res.json();
  },

  async getSongs(params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API_BASE}/songs${query ? `?${query}` : ''}`);
    if (!res.ok) throw new Error('Failed to fetch songs');
    const data = await res.json();
    // Support both old array format and new paginated object format
    return Array.isArray(data) ? data : (data.songs || []);
  },


  async getTrendingSongs() {
    const res = await fetch(`${API_BASE}/songs/trending`);
    if (!res.ok) throw new Error('Failed to fetch trending songs');
    return res.json();
  },

  async getSong(id) {
    const res = await fetch(`${API_BASE}/songs/${id}`);
    if (!res.ok) throw new Error('Failed to fetch song');
    return res.json();
  },

  async createSong(data) {
    const res = await fetch(`${API_BASE}/songs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || 'Failed to create song');
    }
    return res.json();
  },

  async deleteSong(id) {
    const res = await fetch(`${API_BASE}/songs/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete song');
    return res.json();
  },

  async likeSong(id) {
    const res = await fetch(`${API_BASE}/songs/${id}/like`, { method: 'PUT' });
    if (!res.ok) throw new Error('Failed to like song');
    return res.json();
  },

  async getCategories() {
    const res = await fetch(`${API_BASE}/songs/categories`);
    if (!res.ok) throw new Error('Failed to fetch categories');
    return res.json();
  },


  async login(email, password) {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error('Login failed');
    return res.json();
  },

  async googleLogin(credential) {
    const res = await fetch(`${API_BASE}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
    if (!res.ok) throw new Error('Google Login failed');
    return res.json();
  },

  async register(data) {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Registration failed');
    return res.json();
  },
  
  async updateProfile(id, data) {
    const res = await fetch(`${API_BASE}/auth/profile/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update profile');
    return res.json();
  },

  async changePassword(id, data) {
    const res = await fetch(`${API_BASE}/auth/change-password/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to change password');
    }
    return res.json();
  },

  async getUser(id) {
    const res = await fetch(`${API_BASE}/auth/user/${id}`);
    if (!res.ok) throw new Error('Failed to fetch user');
    return res.json();
  },

  async deleteAccount(id) {
    const res = await fetch(`${API_BASE}/auth/delete-account/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete account');
    return res.json();
  },

  async addComment(songId, data) {
    const res = await fetch(`${API_BASE}/songs/${songId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to add comment');
    return res.json();
  },

  async uploadImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Failed to upload image');
    return res.json();
  },
};
